import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  useCreateJobScheduleMutation,
  useDeleteJobScheduleMutation,
  useGetJobScheduleQuery,
  useListJobSchedulesQuery,
  useRunJobScheduleNowMutation,
  useUpdateJobScheduleMutation,
} from "~/ui-rtk/api/jobsApi"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { BROWSER_TZ, TIMEZONE_OPTIONS } from "~/ui-rtk/utils/timezone-options"

const QA_JOB_TYPE = "meta-ads-qa"
const DEFAULT_TIME = "23:59"

/** "59 23 * * *" -> "23:59"; anything non-daily falls back to the default. */
function cronToTime(cron: string): string {
  const match = cron.match(/^(\d{1,2}) (\d{1,2}) \* \* \*$/)
  if (!match) return DEFAULT_TIME
  const [, minute, hour] = match
  return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`
}

/** "23:59" -> "59 23 * * *" (daily). */
function timeToCron(time: string): string {
  const [hour, minute] = time.split(":")
  return `${Number(minute)} ${Number(hour)} * * *`
}

/**
 * Per-company Ads QA setup, embedded in Settings → Ad Blaster (moved out of
 * the AdBlaster page's modal). Creates/updates a recurring `meta-ads-qa`
 * JobSchedule for the CURRENT company (daily, at the chosen time, in the
 * browser's timezone) and optionally routes the Slack findings digest to a
 * custom channel; left empty, the digest goes to the default jobs channel.
 *
 * The channel lives on the schedule's `input` payload. Saves take the atomic
 * PATCH path (cron/timezone/enabled) whenever the channel is unchanged; only
 * a channel change forces the non-atomic delete + create, whose failure mode
 * is surfaced explicitly ("schedule removed, recreation failed").
 */
export default function AdsQaScheduleSection() {
  const { data: schedules, isFetching: isListing } = useListJobSchedulesQuery({
    type: QA_JOB_TYPE,
  })
  const existing = useMemo(() => schedules?.[0], [schedules])
  const { data: fullSchedule } = useGetJobScheduleQuery(existing?.id ?? "", {
    skip: !existing,
  })

  const [createSchedule, { isLoading: isCreating }] =
    useCreateJobScheduleMutation()
  const [updateSchedule, { isLoading: isUpdating }] =
    useUpdateJobScheduleMutation()
  const [deleteSchedule, { isLoading: isDeleting }] =
    useDeleteJobScheduleMutation()
  const [runScheduleNow, { isLoading: isRunningNow }] =
    useRunJobScheduleNowMutation()
  const isSaving = isCreating || isUpdating || isDeleting

  // Fire the saved schedule immediately — same input (custom Slack channel
  // included) and enqueue path as the nightly cron tick. Runs whatever is
  // SAVED, not the unsaved form values.
  const handleRunNow = async () => {
    if (!existing) return
    try {
      await runScheduleNow(existing.id).unwrap()
      toast.success(
        "QA run started with this schedule's saved settings — findings land on the Jobs page and in Slack.",
      )
    } catch {
      toast.error("Could not start the test run. Please try again.")
    }
  }

  const [enabled, setEnabled] = useState(false)
  const [time, setTime] = useState(DEFAULT_TIME)
  const [timezone, setTimezone] = useState(BROWSER_TZ)
  const [channelId, setChannelId] = useState("")

  // Seed the form from the loaded schedule (and re-seed when the
  // input-bearing single-schedule fetch lands).
  useEffect(() => {
    setEnabled(existing?.enabled ?? false)
    setTime(existing ? cronToTime(existing.cron) : DEFAULT_TIME)
    setTimezone(existing?.timezone ?? BROWSER_TZ)
    const input = fullSchedule?.input as { slackChannelId?: string } | undefined
    setChannelId(input?.slackChannelId ?? "")
  }, [existing, fullSchedule])

  const handleSave = async () => {
    const trimmedChannel = channelId.trim()
    if (trimmedChannel && !/^[A-Za-z0-9]{1,32}$/.test(trimmedChannel)) {
      toast.error(
        "Slack channel must be a channel ID like C0123456789 (not a #name).",
      )
      return
    }

    const successToast = () =>
      toast.success(
        `Daily ads QA scheduled at ${time} (${timezone}) — findings go to ${
          trimmedChannel ? "your custom channel" : "the jobs Slack channel"
        }.`,
      )
    const create = () =>
      createSchedule({
        type: QA_JOB_TYPE,
        cron: timeToCron(time),
        timezone,
        input: trimmedChannel ? { slackChannelId: trimmedChannel } : {},
      }).unwrap()

    try {
      if (!enabled) {
        if (existing) {
          await updateSchedule({ id: existing.id, enabled: false }).unwrap()
          toast.success("Daily ads QA disabled for this company.")
        }
        return
      }

      if (existing) {
        // Cron/timezone/enabled are PATCHable — take the atomic path whenever
        // the channel (which lives on the non-PATCHable `input`) is unchanged.
        const loadedInput = fullSchedule?.input as
          | { slackChannelId?: string }
          | undefined
        const channelChanged =
          trimmedChannel !== (loadedInput?.slackChannelId ?? "")
        if (!channelChanged) {
          await updateSchedule({
            id: existing.id,
            enabled: true,
            cron: timeToCron(time),
            timezone,
          }).unwrap()
          successToast()
          return
        }

        // Channel changed → input must be rewritten, and input isn't
        // PATCHable, so this is an unavoidable two-step delete + create. If
        // the create fails after the delete committed, say EXACTLY that —
        // a generic "try again" would hide that the schedule is now gone.
        await deleteSchedule(existing.id).unwrap()
        try {
          await create()
        } catch {
          toast.error(
            "The previous schedule was removed but the new one could not be created — save again to re-enable daily QA.",
          )
          return
        }
        successToast()
        return
      }

      await create()
      successToast()
    } catch {
      toast.error("Could not save the QA schedule. Please try again.")
    }
  }

  return (
    <section className="rounded-lg border border-system-blue-700 bg-system-blue-800 text-white">
      <div className="border-b border-system-blue-700 px-system-lg py-system-md">
        <h2 className="text-lg font-semibold">Ads QA schedule</h2>
        <p className="text-system-gray-300 text-xs">
          Daily read-only QA of this company&apos;s newly-created Meta ads. It
          reports issues and, for ads whose UTMs don&apos;t match the company
          pattern (Settings → Company → UTM parameters), suggests the expected
          URL parameters — applied only when you click Apply on the job run.
        </p>
      </div>

      <div className="flex flex-col gap-system-lg px-system-lg py-system-md">
        {isListing && !schedules ? (
          <p className="text-sm text-system-gray-300">Loading…</p>
        ) : (
          <>
            <label className="flex cursor-pointer items-center gap-system-sm">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
              />
              <span className="text-sm">Run ads QA daily for this company</span>
            </label>

            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="ads-qa-time"
                className="text-sm font-medium text-system-gray-200"
              >
                Run at
              </label>
              <p className="text-system-gray-300 text-xs">
                Once a day, in the selected timezone. Each run reviews the ads
                created since the previous run&apos;s window.
              </p>
              <div className="flex items-center gap-system-sm">
                <input
                  id="ads-qa-time"
                  type="time"
                  value={time}
                  disabled={!enabled}
                  onChange={e => setTime(e.target.value || DEFAULT_TIME)}
                  className="input input-bordered h-10 w-40 bg-system-blue-900 py-0 text-sm disabled:opacity-50"
                />
                <Select
                  id="ads-qa-timezone"
                  options={TIMEZONE_OPTIONS}
                  value={timezone}
                  disabled={!enabled}
                  onChange={e => setTimezone(e.target.value)}
                />
              </div>
            </section>

            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="ads-qa-channel"
                className="text-sm font-medium text-system-gray-200"
              >
                Slack channel for findings (optional)
              </label>
              <p className="text-system-gray-300 text-xs">
                Channel ID (e.g. C0123456789) the findings digest is posted to.
                Leave empty to use the default jobs channel. The Marathon bot
                must be invited to the channel.
              </p>
              <Input
                id="ads-qa-channel"
                value={channelId}
                onChange={e => setChannelId(e.target.value)}
                placeholder="Defaults to the jobs channel"
              />
            </section>
          </>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-system-blue-700 px-system-lg py-system-md">
        {/* Test-run the SAVED schedule (input + channel) without waiting for
            the cron tick. Hidden until a schedule exists to fire. */}
        {existing ? (
          <button
            type="button"
            onClick={() => void handleRunNow()}
            disabled={isRunningNow || isSaving}
            className="rounded-md border border-system-blue-600 px-system-md py-system-2xs text-sm font-medium text-system-gray-200 hover:bg-system-blue-700 hover:text-white disabled:opacity-50"
            title="Fire this schedule immediately with its saved settings"
          >
            {isRunningNow ? "Starting…" : "Run now"}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || isListing}
          className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  )
}
